import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, Minus, BarChart3, Search } from "lucide-react";
import { useData } from "@/contexts/DataContext";
import { getEstimatedListingPrice, getGmpPercentage } from "@/lib/gmpUtils";
import GMPChart from "@/components/GMPChart";
import { useState, type FormEvent } from "react";
import { formatDisplayDate } from "@/lib/dateFormat";
import { formatInr, formatSignedInr } from "@/lib/numberFormat";

const IPOGmp = () => {
  const { ipos } = useData();
  const [expandedIPO, setExpandedIPO] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const normalizedSearch = searchTerm.trim().toLowerCase();
  const filteredIpos = !normalizedSearch
    ? ipos
    : ipos.filter((ipo) => {
        const searchHaystack = [
          ipo.company,
          ipo.status,
          ipo.subscriptionStatus,
          ipo.priceBand,
          ipo.openDate,
          ipo.closeDate,
          formatDisplayDate(ipo.openDate),
          formatDisplayDate(ipo.closeDate),
          ipo.listingDate,
          ipo.allotmentDate,
        ]
          .join(" ")
          .toLowerCase();

        return searchHaystack.includes(normalizedSearch);
      });

  const toggleChart = (ipoId: string) => {
    setExpandedIPO(expandedIPO === ipoId ? null : ipoId);
  };

  const onSearchSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };
  const totalPages = Math.max(1, Math.ceil(filteredIpos.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedIpos = filteredIpos.slice((safePage - 1) * pageSize, safePage * pageSize);

  return (
    <div className="py-10 md:py-14">
      <div className="container mx-auto px-4">
        <h1 className="section-title mb-2">IPO GMP Live</h1>
        <p className="text-muted-foreground mb-8 max-w-xl">Today's IPO GMP live updates, grey market premium trends, subscription status, and expected listing price details.</p>

        <form onSubmit={onSearchSubmit} className="mb-8 flex flex-col sm:flex-row gap-2 sm:items-center">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by IPO name, date, status, subscription..."
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-input bg-background text-sm"
            />
          </div>
          <button
            type="submit"
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
          >
            <Search size={15} />
            Search
          </button>
        </form>

        <div className="grid gap-4">
          {paginatedIpos.map((ipo, i) => {
            const gmpChange = ipo.gmp - ipo.yesterdayGmp;
            const isUp = gmpChange > 0;
            const isDown = gmpChange < 0;
            const pct = getGmpPercentage(ipo.gmp, ipo.priceBand);
            return (
              <motion.div key={ipo.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                <Link to={`/ipos/${ipo.id}`} className="finance-card block p-5">
                  <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0 md:flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-display font-semibold text-foreground">{ipo.company}</h3>
                        <span className={
                          ipo.status === "upcoming" ? "badge-info" :
                          ipo.status === "ongoing" ? "badge-success" :
                          ipo.status === "closing_today" ? "badge-warning" :
                          ipo.status === "allotment" ? "badge-secondary" :
                          ipo.status === "allotted" ? "badge-success" :
                          ipo.status === "listing_today" ? "badge-primary" :
                          ipo.status === "listed" ? "badge-success" :
                          ipo.status === "closed" ? "badge-destructive" :
                          "badge-warning"
                        }>
                          {ipo.status.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span>₹{ipo.priceBand}</span>
                        <span>Lot: {ipo.lotSize}</span>
                        <span>Sub: {ipo.subscriptionStatus || "-"}</span>
                        <span>Open: {formatDisplayDate(ipo.openDate)}</span>
                        <span>Close: {formatDisplayDate(ipo.closeDate)}</span>
                      </p>
                    </div>

                    <div className="w-full md:w-auto md:min-w-[520px]">
                      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 md:gap-4">
                        <div className="text-center">
                          <span className="text-xs text-muted-foreground block">Today GMP</span>
                          <span className={`text-lg font-bold whitespace-nowrap ${ipo.gmp >= 0 ? "text-success" : "text-destructive"}`}>{formatSignedInr(ipo.gmp)}</span>
                          <span className={`text-xs font-medium ml-1 ${ipo.gmp >= 0 ? "text-success" : "text-destructive"}`}>({pct}%)</span>
                        </div>
                        <div className="text-center">
                          <span className="text-xs text-muted-foreground block">Yesterday</span>
                          <span className={`text-lg font-semibold whitespace-nowrap ${ipo.yesterdayGmp >= 0 ? "text-foreground" : "text-destructive"}`}>{formatSignedInr(ipo.yesterdayGmp)}</span>
                          <span className="text-xs font-medium text-muted-foreground ml-1">({getGmpPercentage(ipo.yesterdayGmp, ipo.priceBand)}%)</span>
                        </div>
                        <div className="text-center">
                          <span className="text-xs text-muted-foreground block">Change</span>
                          <span className={`text-lg font-semibold flex items-center justify-center gap-1 whitespace-nowrap ${isUp ? "text-success" : isDown ? "text-destructive" : "text-muted-foreground"}`}>
                            {isUp ? <TrendingUp size={16} /> : isDown ? <TrendingDown size={16} /> : <Minus size={16} />}
                            {formatSignedInr(gmpChange)}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="text-xs text-muted-foreground block">Est. Listing</span>
                          <span className="text-lg font-semibold text-foreground whitespace-nowrap">{formatInr(getEstimatedListingPrice(ipo.priceBand, ipo.gmp, ipo.estimatedListing))}</span>
                        </div>
                      </div>
                    </div>

                    <div className="flex justify-end md:self-center">
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          toggleChart(ipo.id);
                        }}
                        className="p-2 rounded hover:bg-muted transition-colors"
                        title="View GMP Chart"
                      >
                        <BarChart3 size={16} className={expandedIPO === ipo.id ? "text-primary" : "text-muted-foreground"} />
                      </button>
                    </div>
                  </div>
                  
                  {/* GMP Chart */}
                  {expandedIPO === ipo.id && (
                    <motion.div 
                      initial={{ opacity: 0, height: 0 }} 
                      animate={{ opacity: 1, height: "auto" }} 
                      transition={{ duration: 0.3 }}
                      className="mt-4 pt-4 border-t border-border"
                    >
                      <div className="bg-muted/20 rounded-lg p-4">
                        <h4 className="text-sm font-medium mb-3 text-foreground">GMP History Chart</h4>
                        <GMPChart ipoId={ipo.id} />
                      </div>
                    </motion.div>
                  )}
                </Link>
              </motion.div>
            );
          })}
        </div>
        {filteredIpos.length === 0 && (
          <div className="text-center py-8 text-sm text-muted-foreground">
            No GMP records found for this search.
          </div>
        )}
        {filteredIpos.length > 0 && (
          <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
            <p className="text-xs text-muted-foreground">
              Showing {(safePage - 1) * pageSize + 1}-{Math.min(safePage * pageSize, filteredIpos.length)} of {filteredIpos.length}
            </p>
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setCurrentPage(Math.max(1, safePage - 1))}
                disabled={safePage === 1}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1.5 rounded-lg text-xs border ${
                    safePage === page ? "bg-primary text-primary-foreground border-primary" : "border-input"
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setCurrentPage(Math.min(totalPages, safePage + 1))}
                disabled={safePage === totalPages}
                className="px-3 py-1.5 rounded-lg border border-input text-xs disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </div>
        )}

        <div className="mt-8 p-4 rounded-lg bg-muted/50 border border-border">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong>Disclaimer:</strong> IPO Grey Market Premium (GMP) information shared on this website is based on market sources, industry discussions, and publicly available information. GMP is unofficial, not regulated by SEBI, and may change without notice. We do not guarantee accuracy or future returns.
          </p>
        </div>
      </div>
    </div>
  );
};

export default IPOGmp;
